import { Image } from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { isOfficialLoginMethod, type LoginRequest, type Session } from '@/api';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { takePostLoginPath } from '@/lib/post-login';
import { AddressesScreen } from '@/pages/Account/Addresses/Addresses.page';
import { BillingScreen } from '@/pages/Account/Billing/Billing.page';
import { DevelopersScreen } from '@/pages/Account/Developers/Developers.page';
import { CreateFormScreen } from '@/pages/Account/Forms/CreateForm.page';
import { FormsListScreen } from '@/pages/Account/Forms/List.page';
import { FormMessagesScreen } from '@/pages/Account/Forms/Messages.page';
import { FormOverviewScreen } from '@/pages/Account/Forms/Overview.page';
import { HomeScreen } from '@/pages/Account/Home/Home.page';
import { EditIntegrationScreen } from '@/pages/Account/Integrations/Edit.page';
import { DeliveriesScreen } from '@/pages/Account/Integrations/IntegrationDeliveries.page';
import { IntegrationsListScreen } from '@/pages/Account/Integrations/List.page';
import { NewIntegrationScreen } from '@/pages/Account/Integrations/New.page';
import { InboxScreen } from '@/pages/Account/Messages/Inbox.page';
import { VolumeScreen } from '@/pages/Account/Messages/Volume.page';
import { AccountNotFoundScreen } from '@/pages/Account/NotFound.page';
import { ProblemsScreen } from '@/pages/Account/Problems/Problems.page';
import { SecurityScreen } from '@/pages/Account/Security/Security.page';
import { Login2FAPage } from '@/pages/Login2FAPage';
import { LoginCallbackPage } from '@/pages/LoginCallbackPage';
import { LoginPage } from '@/pages/LoginPage';
import { MagicLinkLoginPage } from '@/pages/MagicLinkLoginPage';
import { NewsPage } from '@/pages/NewsPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { SupportPage } from '@/pages/SupportPage';
import { TermsPage } from '@/pages/TermsPage';
import { API_BASE_URL } from '../../constants';
import { AccountConnectedAccountsLandingPage } from '../../pages/Account/ConnectedAccountsLanding.page';
import { AccountDocsPage } from '../../pages/Account/Docs.page';
import { AccountFormFieldsPage } from '../../pages/Account/Forms/Fields.page';
import { DocsPage } from '../../pages/Docs.page';
import { HomePage } from '../../pages/Home.page';
import { IntegrationDetailPage } from '../../pages/IntegrationDetail.page';
import { IntegrationsPage } from '../../pages/Integrations.page';
import { PricingPage } from '../../pages/Pricing.page';
import { useRustyState } from '../../state';
import classes from '../Common/Title.module.css';
import { AccountLayout } from './Account';
import { Footer } from './Footer';
import { PublicLayout } from './PublicLayout';
import { LegacyProfileRedirect, RedirectTo, RememberNext, RequireAuth } from './RouteGuards';

interface LanguageRoutesProps {
  languagePrefix?: string;
}

export function LanguageRoutes({ languagePrefix }: LanguageRoutesProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const afterLogin = () => navigate(takePostLoginPath() ?? createLanguageURL('/account'));

  const brandLogo = <Image src="/logo.svg" alt={t('brand.logoAlt')} w={48} h={48} fit="contain" />;
  const brandName = t('brand.name');

  React.useEffect(() => {
    if (languagePrefix && languagePrefix !== i18n.language) {
      i18n.changeLanguage(languagePrefix);
    } else if (!languagePrefix && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [languagePrefix, i18n]);

  // Refresh identity claims from /v1/a/whoami on boot and on
  // tab-focus / visibility transitions so the account widget shows the
  // freshest email / displayName without periodic polling. Debounced to a
  // 5-second minimum interval because focus events fire aggressively.
  React.useEffect(() => {
    const auth = useRustyState.getState().api?.auth;
    if (!auth) return;

    let lastCallAt = 0;
    const minIntervalMs = 5_000;

    const callWhoami = () => {
      const now = Date.now();
      if (now - lastCallAt < minIntervalMs) return;
      if (!auth.hasValidAccessToken()) return;
      lastCallAt = now;
      auth.whoami().catch((err: unknown) => {
        console.warn('whoami refresh failed; non-fatal', err);
      });
    };

    // Boot: kick off once we know we have a valid access token. Don't block
    // initial render — the call is fire-and-forget.
    callWhoami();

    const onFocus = () => callWhoami();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') callWhoami();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const setSession = (session: Session) => {
    useRustyState.getState().api.auth?.setSession(session);
  };

  const loginPage = (mode: 'signin' | 'signup') => (
    <RememberNext>
      <LoginPage
        mode={mode}
        onLoginSuccess={afterLogin}
        apiBaseUrl={API_BASE_URL}
        login={async (loginRequest: LoginRequest) => {
          if (isOfficialLoginMethod(loginRequest.type)) {
            return await useRustyState.getState().api.auth!.login(loginRequest);
          }
          throw new Error('Unsupported login method');
        }}
        loginChallenge={useRustyState.getState().loginChallenge}
        verify2FA={useRustyState.getState().verify2FA}
        logo={brandLogo}
        brandName={brandName}
      />
    </RememberNext>
  );

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route
          path="/news"
          element={<NewsPage blogBaseUrl="https://blog.formshive.com" footer={<Footer />} />}
        />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/integrations/:slug" element={<IntegrationDetailPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/login" element={loginPage('signin')} />
        <Route
          path="/login/callback"
          element={
            <LoginCallbackPage
              setSession={setSession}
              onLoginSuccess={afterLogin}
              titleClassName={classes.title}
              apiBaseUrl={API_BASE_URL}
            />
          }
        />
        <Route
          path="/login/2fa"
          element={
            <Login2FAPage
              setSession={setSession}
              onLoginSuccess={afterLogin}
              verify2FA={useRustyState.getState().verify2FA}
            />
          }
        />
        <Route
          path="/login/magic-link"
          element={
            <MagicLinkLoginPage
              loginChallenge={useRustyState.getState().loginChallenge}
              onLoginSuccess={afterLogin}
            />
          }
        />
        <Route
          path="/privacy"
          element={<PrivacyPage supportEmail="support@formshive.com" footer={<Footer />} />}
        />
        <Route
          path="/terms"
          element={<TermsPage supportEmail="support@formshive.com" footer={<Footer />} />}
        />
        <Route path="/signup" element={loginPage('signup')} />
      </Route>

      <Route
        path="/account"
        element={
          <RequireAuth>
            <AccountLayout />
          </RequireAuth>
        }
      >
        <Route index element={<HomeScreen />} />
        <Route path="forms" element={<FormsListScreen />} />
        <Route path="forms/new" element={<CreateFormScreen />} />
        <Route path="forms/:id" element={<FormOverviewScreen />} />
        <Route path="forms/:id/fields" element={<AccountFormFieldsPage />} />
        <Route path="forms/:id/messages" element={<FormMessagesScreen />} />
        <Route path="messages" element={<InboxScreen />} />
        <Route path="messages/volume" element={<VolumeScreen />} />
        <Route path="messages/:id" element={<InboxScreen />} />
        <Route path="problems" element={<ProblemsScreen />} />
        <Route path="integrations" element={<IntegrationsListScreen />} />
        <Route path="integrations/new" element={<NewIntegrationScreen />} />
        <Route path="integrations/:id" element={<EditIntegrationScreen />} />
        <Route path="integrations/:id/deliveries" element={<DeliveriesScreen />} />
        <Route path="billing" element={<BillingScreen />} />
        <Route path="addresses" element={<AddressesScreen />} />
        <Route path="security" element={<SecurityScreen />} />
        <Route path="developers" element={<DevelopersScreen />} />
        <Route path="docs" element={<AccountDocsPage />} />
        <Route
          path="support"
          element={<SupportPage session={useRustyState.getState().api?.auth?.getSession()} />}
        />
        <Route path="linked" element={<AccountConnectedAccountsLandingPage />} />

        <Route path="forms/create" element={<RedirectTo to="/account/forms/new" />} />
        <Route path="message/:id" element={<RedirectTo to="/account/messages/:id" />} />
        <Route path="issues" element={<RedirectTo to="/account/problems" />} />
        <Route path="integrations/create" element={<RedirectTo to="/account/integrations/new" />} />
        <Route path="billing/usage" element={<RedirectTo to="/account/billing" />} />
        <Route
          path="billing/subscriptions"
          element={<RedirectTo to="/account/billing" search="?section=plans" />}
        />
        <Route path="profile" element={<LegacyProfileRedirect />} />
        <Route path="connected" element={<RedirectTo to="/account/security" />} />
        <Route
          path="security-log"
          element={<RedirectTo to="/account/security" search="?section=history" />}
        />
        <Route
          path="data-privacy"
          element={<RedirectTo to="/account/security" search="?section=data" />}
        />
        <Route path="api-keys" element={<RedirectTo to="/account/developers" />} />
        <Route path="mcp" element={<RedirectTo to="/account/developers" search="?section=mcp" />} />
        <Route path="*" element={<AccountNotFoundScreen />} />
      </Route>
    </Routes>
  );
}
